import React, { useCallback, useEffect, useState } from 'react'
import { ScrollableFilterListView } from '../../../organisms/ScrollableFilterList/ScrollableFilterList-view'
import styles from './filterList.jcss'
import { cn } from 'ethcss'
import { Icon } from '../../../molecules/Icon'
import translate from '../../../core/translate'
import { FilterInner } from '../FilterInner/FilterInner-view'
import { FilterAdd } from '../FilterAdd/FilterAdd-view'
import { getCompatibleFilters, onApplyFilterToQuery, getSomeValues } from 'core/helpers/filters'
import { FilterQuery, IFilter, IFilterChange, Option } from '../Filter/Filter-types'
import { useLocation } from 'react-router'
import { useSelector } from 'react-redux'
import { ALLOW_ADVANCED_SEARCH } from '../../catalog/catalog'
import { useClickOutside } from 'react-click-outside-hook'
import { AdvancedSearchQuery } from 'core/models/AdvancedSearch'
import { IAuthenticatedUserState } from 'blocks.app/user/user.state'

interface IFilterItemProps {
    item: IFilter
    onSelectFilter: (item: IFilter) => void
    onDelete: (filterName: string) => void
}

interface IFilterListProps {
    selectedFilters: IFilter[]
    setSelectedFilters: (filters: IFilter[]) => void
    availableFilters: IFilter[]
    setAvailableFilters: (filters: IFilter[]) => void
    type: string
    onFilterChange: (query: FilterQuery) => void
    getAdvancedSearch: () => AdvancedSearchQuery
}

const FilterItem = ({ item, onSelectFilter, onDelete }: IFilterItemProps) => {
    return (
        <div className={styles.filterItem} onClick={() => onSelectFilter(item)}>
            <div className={styles.filterWrapper}>
                <Icon type="arrow_down" color="#323232" size={24} />
                <div className={styles.tooltip}>{getSomeValues(item)}</div>
            </div>
            <div className={styles.deleteItem}>
                <Icon
                    type="close"
                    color="#323232"
                    size={13}
                    onClick={(e) => {
                        e.stopPropagation()
                        onDelete(item.name)
                    }}
                    tooltip={{ title: translate('delete') }}
                />
            </div>
        </div>
    )
}

export const FilterList = ({
    selectedFilters,
    setSelectedFilters,
    availableFilters,
    setAvailableFilters,
    type,
    onFilterChange,
    getAdvancedSearch,
}: IFilterListProps) => {
    const [selectedFilter, setSelectedFilter] = useState<IFilter | null>(null)
    const [addModalOpened, setAddModalOpened] = useState<boolean>(false)
    const [filterOptionsOpened, setFilterOptionsOpened] = useState(true)
    const location = useLocation()
    const advancedSearchType = Object.prototype.hasOwnProperty.call(ALLOW_ADVANCED_SEARCH, type)
        ? ALLOW_ADVANCED_SEARCH[type as keyof typeof ALLOW_ADVANCED_SEARCH]
        : null
    const advancedSearchSettings = useSelector((store: { user: IAuthenticatedUserState }) =>
        advancedSearchType ? store.user.data.settings.advancedSearch[advancedSearchType] : undefined
    )
    const [filtersRef, hasClickedOutside] = useClickOutside()

    const onAddFilter = useCallback(
        (filterItem: IFilter) => {
            setSelectedFilters([...selectedFilters, filterItem])
            closeAddModal()
        },
        [selectedFilters]
    )

    const closeAddModal = () => {
        setAddModalOpened(false)
    }

    const onSelectFilter = (filterItem: IFilter) => {
        if (selectedFilter?.name === filterItem.name) {
            setSelectedFilter(null)
            setFilterOptionsOpened(false)
        } else {
            setSelectedFilter(filterItem)
            setFilterOptionsOpened(true)
        }
    }

    const onCloseFilterInner = () => {
        if (selectedFilter) {
            onApply()
        }
        setSelectedFilter(null)
    }

    const changeVariant = useCallback(
        (filterItem: IFilterChange) => {
            setSelectedFilters(
                selectedFilters.map((filter: IFilter) => {
                    if (filter.name === filterItem.name) {
                        const options = filter.options.map((option: Option) => {
                            if (filterItem.option.label === option.label) {
                                return filterItem.option
                            }

                            return option
                        })

                        return {
                            ...filter,
                            options,
                        }
                    }

                    return filter
                })
            )
        },
        [selectedFilters]
    )

    const multiselect = useCallback(
        (filterName: string, state: boolean) => {
            setSelectedFilters(
                selectedFilters.map((filter: IFilter) => {
                    if (filter.name === filterName) {
                        return {
                            ...filter,
                            options: filter.options.map((option: Option) => ({ ...option, selected: state })),
                        }
                    }

                    return filter
                })
            )
        },
        [selectedFilters]
    )

    const onDelete = useCallback(
        (filterName: string) => {
            const filteredItems = selectedFilters.filter((filter: IFilter) => filter.name !== filterName)

            setSelectedFilters(filteredItems)
            onFilterChange(onApplyFilterToQuery(filteredItems, advancedSearchSettings))
        },
        [selectedFilters, advancedSearchSettings]
    )

    const onApply = useCallback(() => {
        getCompatibleFilters(selectedFilters, location, type, getAdvancedSearch()).then((res) => {
            const filteredArray = res.filter((element) => selectedFilters.some((item) => element.name === item.name))

            setAvailableFilters(res)
            setSelectedFilters(filteredArray)
            onFilterChange(onApplyFilterToQuery(filteredArray, advancedSearchSettings))
        })
        setSelectedFilter(null)
    }, [selectedFilters, location, advancedSearchSettings])

    useEffect(() => {
        if (selectedFilter) {
            setSelectedFilter(selectedFilters.find((filter) => filter.name === selectedFilter.name) ?? null)
        }
    }, [selectedFilters])

    useEffect(() => {
        if (hasClickedOutside) {
            onCloseFilterInner()
        }
    }, [hasClickedOutside])

    return (
        <div className={styles.filtersBlock}>
            <div ref={filtersRef} className={styles.filterListWrapper}>
                <ScrollableFilterListView refreshArrowsTrigger={selectedFilters}>
                    <div className={cn(styles.filterList)}>
                        {selectedFilters.map((item: IFilter, index: number) => {
                            return (
                                <FilterItem
                                    key={index}
                                    item={item}
                                    onSelectFilter={onSelectFilter}
                                    onDelete={onDelete}
                                />
                            )
                        })}
                    </div>
                </ScrollableFilterListView>
                {filterOptionsOpened && selectedFilter && (
                    <>
                        <FilterInner
                            filter={selectedFilter}
                            changeVariant={changeVariant}
                            multiselect={multiselect}
                            onApply={onApply}
                            onClose={onCloseFilterInner}
                        />
                    </>
                )}
            </div>
            {selectedFilters.length < availableFilters.length && (
                <div className={styles.addFilter} onClick={() => setAddModalOpened(true)}>
                    + {translate('addFilter')}
                </div>
            )}
            {addModalOpened && (
                <FilterAdd
                    availableFilters={availableFilters}
                    filterList={selectedFilters}
                    onAddFilter={onAddFilter}
                    onClose={closeAddModal}
                />
            )}
        </div>
    )
}
